import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  useTheme,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp,
  Psychology,
  Assessment,
  TableChart,
  Speed,
  Group,
  Chat,
  Timeline,
  Insights,
  QueryStats,
  BarChart,
  PieChart,
  AccountTree,
  Timer,
  CompareArrows,
  ShowChart,
  Grade,
  AttachMoney,
  DeviceHub,
  Timelapse,
  Person,
  TrendingDown,
  Dashboard,
  Message,
  CheckCircle,
  Assignment,
  Star,
  Repeat,
  Shield,
  FileDownload,
  Hub,
  Label as LabelIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';

import { OverviewDashboard } from '../components/Reports/OverviewDashboard';
import { ConversationAnalytics } from '../components/Reports/ConversationAnalytics';
import { SentimentAnalysis } from '../components/Reports/SentimentAnalysis';
import { AgentPerformance } from '../components/Reports/AgentPerformance';
import { ConversationAudits } from '../components/Reports/ConversationAudits';
import { CSATAnalytics } from '../components/Reports/CSATAnalytics';
import { ChannelAnalytics } from '../components/Reports/ChannelAnalytics';
import { AggregatedReports } from '../components/Reports/AggregatedReports';
import CustomerJourneyAnalytics from '../components/Reports/CustomerJourneyAnalytics';
import SLAComplianceTracking from '../components/Reports/SLAComplianceTracking';
import TeamPerformanceAnalytics from '../components/Reports/TeamPerformanceAnalytics';
import PredictiveAnalytics from '../components/Reports/PredictiveAnalytics';
import ConversationQualityScoring from '../components/Reports/ConversationQualityScoring';
import RevenueImpactAnalysis from '../components/Reports/RevenueImpactAnalysis';
import MultiChannelAttribution from '../components/Reports/MultiChannelAttribution';
import PeakLoadCapacityPlanning from '../components/Reports/PeakLoadCapacityPlanning';
import AgentProductivityWorkload from '../components/Reports/AgentProductivityWorkload';
import CustomerRetentionChurn from '../components/Reports/CustomerRetentionChurn';
import { LabelAnalytics } from '../components/Reports/LabelAnalytics';
import { advancedReportsAPI, agentsAPI, inboxesAPI } from '../services/api';
import { PermissionGuard } from '../components/Permissions/PermissionGuard'; // 14/11/2025 - User Access Management Module Phase 5.2.3
import {
  exportToCSV,
  exportToPDF,
  exportToExcel,
  exportToPDFWithScreenshot,
  extractTableDataFromDOM,
  ExportData
} from '../utils/exportUtils';

// Category and Sub-tab interfaces
interface ReportTab {
  label: string;
  icon: React.ReactElement;
  component: React.ReactElement;
  description?: string;
}

interface ReportCategory {
  label: string;
  icon: React.ReactElement;
  color: string;
  description: string;
  tabs: ReportTab[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportsPage: React.FC = () => {
  const theme = useTheme();
  const [categoryTab, setCategoryTab] = useState(0);
  const [subTabs, setSubTabs] = useState<{ [key: number]: number }>({
    0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });
  const [selectedAgent, setSelectedAgent] = useState<number | 'all'>('all');
  const [selectedChannel, setSelectedChannel] = useState<number | 'all'>('all');

  // Filter data state
  const [agents, setAgents] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(true);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setFiltersLoading(true);
      try {
        const [agentsData, channelsData] = await Promise.all([
          agentsAPI.getAgents(),
          inboxesAPI.getInboxes()
        ]);

        setAgents(agentsData.agents || agentsData || []);
        setChannels(channelsData.inboxes || channelsData || []);
      } catch (err: any) {
        console.error('Failed to load filter options:', err);
        setError('Failed to load filter options. Using defaults.');
      } finally {
        setFiltersLoading(false);
      }
    };

    fetchFilterOptions();
  }, []);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
    setCategoryTab(newValue);
  };

  const handleSubTabChange = (categoryIndex: number) => (event: React.SyntheticEvent, newValue: number) => {
    setSubTabs({ ...subTabs, [categoryIndex]: newValue });
  };

  const handleExport = async (format: 'csv' | 'pdf' | 'excel') => {
    try {
      const currentCategory = reportCategories[categoryTab];
      const currentSubTab = subTabs[categoryTab] || 0;
      const currentReport = currentCategory.tabs[currentSubTab];

      console.log(`Export: Category ${categoryTab}, SubTab ${currentSubTab}, Report: ${currentReport.label}`);

      // Find the visible panel using the sub-tab index (not global index)
      // Each category has its own set of TabPanels with indices 0, 1, 2, 3...
      const panel = document.getElementById(`reports-tabpanel-${currentSubTab}`);

      if (!panel) {
        const availablePanels = Array.from(document.querySelectorAll('[role="tabpanel"]')).map(p => p.id);
        console.error('Panel not found. Available panels:', availablePanels);
        throw new Error(`Could not find report panel with index ${currentSubTab}. Available panels: ${availablePanels.join(', ')}`);
      }

      // Check if panel is actually visible (not hidden)
      const isHidden = panel.hasAttribute('hidden') && panel.getAttribute('hidden') !== 'false';
      if (isHidden) {
        console.error('Panel found but is hidden:', panel.id, 'hidden attr:', panel.getAttribute('hidden'));
        throw new Error(`Report panel ${currentSubTab} is not currently visible. Please ensure you're on the correct tab.`);
      }

      const visiblePanel = panel as HTMLElement;
      console.log(`Found visible panel: ${visiblePanel.id}`);

      const reportName = `${currentCategory.label} - ${currentReport.label}`;
      const reportDateRange = {
        startDate: dateRange.startDate.toISOString().split('T')[0],
        endDate: dateRange.endDate.toISOString().split('T')[0]
      };

      // For PDF format, use screenshot capture
      if (format === 'pdf') {
        console.log('Capturing PDF screenshot...');
        await exportToPDFWithScreenshot(reportName, visiblePanel, reportDateRange);
        console.log(`Successfully exported ${currentReport.label} as PDF with screenshots`);
        return;
      }

      // For CSV and Excel, extract data from the visible panel
      const exportData: ExportData = {
        reportName,
        dateRange: reportDateRange,
        metrics: [],
        tables: [],
        charts: [],
        additionalInfo: currentReport.description
      };

      // Extract metrics from Card components in the visible panel
      const metricCards = visiblePanel.querySelectorAll('.MuiCard-root');
      metricCards.forEach((card) => {
        const label = card.querySelector('.MuiTypography-body2, .MuiTypography-caption')?.textContent?.trim();
        const value = card.querySelector('.MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6, .MuiTypography-h3')?.textContent?.trim();

        if (label && value) {
          // Filter out non-metric cards (like report counts)
          const labelLower = label.toLowerCase();
          if (!labelLower.includes('report') &&
              !labelLower.includes('generated') &&
              !labelLower.includes('copyright') &&
              value !== currentReport.label) {
            exportData.metrics?.push({ label, value });
          }
        }
      });

      // Extract tables from the visible panel
      const tables = visiblePanel.querySelectorAll('table.MuiTable-root');
      tables.forEach((table, index) => {
        const tableElement = table as HTMLTableElement;
        const tableData = extractTableDataFromDOM(tableElement);

        if (tableData.headers.length > 0 && tableData.rows.length > 0) {
          // Try to find table title from nearby Typography element
          let titleElement = table.closest('.MuiPaper-root')?.querySelector('.MuiTypography-h6, .MuiTypography-h5, .MuiTypography-h4');

          // If not found, try parent container
          if (!titleElement) {
            const parent = table.parentElement;
            titleElement = parent?.querySelector('.MuiTypography-h6, .MuiTypography-h5, .MuiTypography-h4');
          }

          const title = titleElement?.textContent?.trim() || `Data Table ${index + 1}`;

          exportData.tables?.push({
            title,
            headers: tableData.headers,
            rows: tableData.rows
          });
        }
      });

      // If no tables or metrics found, add a note
      if (exportData.tables?.length === 0 && exportData.metrics?.length === 0) {
        exportData.additionalInfo = `${exportData.additionalInfo}\n\nNote: This report contains primarily visual charts. For complete visual export, please use PDF format.`;
      }

      // Execute export based on format
      switch (format) {
        case 'csv':
          exportToCSV(exportData);
          break;
        case 'excel':
          exportToExcel(exportData);
          break;
        default:
          throw new Error('Invalid export format');
      }

      console.log(`Successfully exported ${currentReport.label} as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      alert(`Failed to export report. Please try again.\n\nError: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Define all report categories with their sub-tabs
  const reportCategories: ReportCategory[] = [
    {
      label: 'Overview & Executive',
      icon: <Dashboard />,
      color: '#1976d2', // Blue
      description: 'Big-picture summaries for leadership and analytics overviews',
      tabs: [
        {
          label: 'Overview',
          icon: <AnalyticsIcon />,
          component: <OverviewDashboard dateRange={dateRange} />,
          description: 'All-in-one executive summary of key metrics'
        },
        {
          label: 'Aggregated Reports',
          icon: <BarChart />,
          component: <AggregatedReports dateRange={dateRange} />,
          description: 'Consolidated data across teams, channels, or time periods'
        },
        {
          label: 'Revenue Impact',
          icon: <AttachMoney />,
          component: <RevenueImpactAnalysis
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
          />,
          description: 'How conversations, agents, and channels contribute to revenue'
        },
        {
          label: 'Predictive Analytics',
          icon: <ShowChart />,
          component: <PredictiveAnalytics
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
            forecastDays={30}
          />,
          description: 'AI-driven trends, forecasts, and what-if scenarios'
        }
      ]
    },
    {
      label: 'Conversation Intelligence',
      icon: <Message />,
      color: '#7c4dff', // Purple
      description: 'Deep-dive into conversations, tone, and compliance',
      tabs: [
        {
          label: 'Conversation Analytics',
          icon: <QueryStats />,
          component: <ConversationAnalytics
            dateRange={dateRange}
            selectedAgent={selectedAgent}
            selectedChannel={selectedChannel}
          />,
          description: 'Volume, topics, durations, and engagement trends'
        },
        {
          label: 'Sentiment Analysis',
          icon: <Psychology />,
          component: <SentimentAnalysis
            dateRange={dateRange}
            selectedAgent={selectedAgent}
            selectedChannel={selectedChannel}
          />,
          description: 'Sentiment breakdown by customer tone and context'
        },
        {
          label: 'Conversation Quality',
          icon: <Grade />,
          component: <ConversationQualityScoring
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
          />,
          description: 'Quality and adherence metrics (response relevance, tone, empathy)'
        },
        {
          label: 'Conversation Audits',
          icon: <Assignment />,
          component: <ConversationAudits
            dateRange={dateRange}
            selectedAgent={selectedAgent}
          />,
          description: 'Manual/AI audits for compliance and QA scoring'
        },
        {
          label: 'Label Analytics',
          icon: <LabelIcon />,
          component: <LabelAnalytics
            dateRange={dateRange}
            selectedAgent={selectedAgent}
            selectedChannel={selectedChannel}
          />,
          description: 'Label usage, distribution, and performance analytics'
        }
      ]
    },
    {
      label: 'Agent & Team Performance',
      icon: <Group />,
      color: '#4caf50', // Green
      description: 'Track how individuals and teams perform operationally',
      tabs: [
        {
          label: 'Agent Performance',
          icon: <Person />,
          component: <AgentPerformance dateRange={dateRange} />,
          description: 'Agent-specific KPIs — average handle time, CSAT, FCR'
        },
        {
          label: 'Team Performance',
          icon: <CompareArrows />,
          component: <TeamPerformanceAnalytics
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
          />,
          description: 'Aggregated metrics per team or department'
        },
        {
          label: 'Agent Productivity',
          icon: <Speed />,
          component: <AgentProductivityWorkload
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
          />,
          description: 'Workload balance, idle time, occupancy'
        },
        {
          label: 'Capacity Planning',
          icon: <Timelapse />,
          component: <PeakLoadCapacityPlanning
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
          />,
          description: 'Forecast staffing needs vs conversation volume'
        }
      ]
    },
    {
      label: 'Customer Experience',
      icon: <Star />,
      color: '#ff9800', // Orange
      description: 'Gauge happiness, loyalty, and end-to-end journey',
      tabs: [
        {
          label: 'CSAT Analytics',
          icon: <TrendingUp />,
          component: <CSATAnalytics dateRange={dateRange} />,
          description: 'Satisfaction scores and breakdown by category/channel'
        },
        {
          label: 'Customer Journey',
          icon: <AccountTree />,
          component: <CustomerJourneyAnalytics
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
            channelId={selectedChannel !== 'all' ? String(selectedChannel) : undefined}
          />,
          description: 'Touchpoint mapping from first contact to resolution'
        },
        {
          label: 'Customer Retention',
          icon: <Repeat />,
          component: <CustomerRetentionChurn
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
          />,
          description: 'Churn rates, returning customers, and loyalty metrics'
        }
      ]
    },
    {
      label: 'Channel & Attribution',
      icon: <Hub />,
      color: '#00bcd4', // Cyan
      description: 'Measure which channels perform best and why',
      tabs: [
        {
          label: 'Channel Analytics',
          icon: <PieChart />,
          component: <ChannelAnalytics dateRange={dateRange} />,
          description: 'Channel-wise breakdown — chat, email, social, etc.'
        },
        {
          label: 'Multi-Channel Attribution',
          icon: <DeviceHub />,
          component: <MultiChannelAttribution
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
          />,
          description: 'Conversion and engagement attribution across touchpoints'
        }
      ]
    },
    {
      label: 'Compliance & Operations',
      icon: <Shield />,
      color: '#757575', // Gray
      description: 'Monitor SLA adherence, standards, and operational health',
      tabs: [
        {
          label: 'SLA Compliance',
          icon: <Timer />,
          component: <SLAComplianceTracking
            startDate={dateRange.startDate.toISOString().split('T')[0]}
            endDate={dateRange.endDate.toISOString().split('T')[0]}
            agentId={selectedAgent !== 'all' ? String(selectedAgent) : undefined}
          />,
          description: 'SLA metrics, breaches, and response/resolution timelines'
        }
      ]
    }
  ];

  const currentCategory = reportCategories[categoryTab];
  const currentSubTab = subTabs[categoryTab] || 0;
  const currentReport = currentCategory.tabs[currentSubTab];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1
        }}>
          <AnalyticsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h1">
              Advanced Analytics & Reports
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentCategory.description}
            </Typography>
          </Box>

          {/* Global Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <MUIDatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date || prev.startDate }))}
              slotProps={{ textField: { size: 'small' } }}
            />
            <MUIDatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date || prev.endDate }))}
              slotProps={{ textField: { size: 'small' } }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Agent</InputLabel>
              <Select
                value={selectedAgent}
                label="Agent"
                onChange={(e) => setSelectedAgent(e.target.value as number | 'all')}
                disabled={filtersLoading}
              >
                <MenuItem value="all">All Agents</MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name || agent.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={selectedChannel}
                label="Channel"
                onChange={(e) => setSelectedChannel(e.target.value as number | 'all')}
                disabled={filtersLoading}
              >
                <MenuItem value="all">All Channels</MenuItem>
                {channels.map((channel) => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel.name || channel.channel_type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Export Buttons */}
            <PermissionGuard permission="reports:export">
              <Tooltip title="Export as CSV">
                <IconButton size="small" onClick={() => handleExport('csv')}>
                  <FileDownload />
                </IconButton>
              </Tooltip>
            </PermissionGuard>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Category-Level Tabs */}
        <Paper sx={{ width: '100%', bgcolor: 'background.paper', mb: 2 }}>
          <Tabs
            value={categoryTab}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                px: 3
              }
            }}
          >
            {reportCategories.map((category, index) => (
              <Tab
                key={index}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: categoryTab === index ? category.color : 'inherit'
                    }}>
                      {category.icon}
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {category.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {category.tabs.length} {category.tabs.length === 1 ? 'report' : 'reports'}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        {/* Sub-Tabs (Report Tabs within Category) */}
        <Paper sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {/* Current Report Info Bar */}
          <Box sx={{
            p: 2,
            bgcolor: `${currentCategory.color}10`,
            borderLeft: `4px solid ${currentCategory.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ color: currentCategory.color }}>
                {currentReport.icon}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: currentCategory.color }}>
                  {currentReport.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentReport.description}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={() => handleExport('csv')}
              >
                CSV
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={() => handleExport('pdf')}
              >
                PDF
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={() => handleExport('excel')}
              >
                Excel
              </Button>
            </Box>
          </Box>

          {/* Sub-Tabs Navigation */}
          {currentCategory.tabs.length > 1 && (
            <Tabs
              value={currentSubTab}
              onChange={handleSubTabChange(categoryTab)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                },
                '& .Mui-selected': {
                  color: currentCategory.color
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: currentCategory.color
                }
              }}
            >
              {currentCategory.tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.icon}
                      {tab.label}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          )}

          {/* Report Content */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {currentCategory.tabs.map((tab, index) => (
                <TabPanel key={index} value={currentSubTab} index={index}>
                  {tab.component}
                </TabPanel>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ReportsPage;
